import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { asList, getJson, integrationError, postJson, putJson, statusClass } from '../helpers';
import { integrationLabel, integrationSpec } from '../../utils/integrationRoles';

const TYPE_OPTIONS = [
  { value: 'quickbooks_online', label: 'QuickBooks Online' },
  { value: 'procore', label: 'Procore' },
  { value: 'oauth', label: 'OAuth' },
  { value: 'api_key', label: 'API Key' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'basic_auth', label: 'Basic Auth' },
  { value: 'custom', label: 'Custom' },
];

const ROLE_OPTIONS = [
  { value: '', label: 'Infer from type' },
  { value: 'erp', label: 'ERP (general ledger)' },
  { value: 'project_tool', label: 'Project tool' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'error', label: 'Error' },
];

function blankForm() {
  return {
    integration_name: '',
    integration_type: '',
    client_id: '',
    client_secret: '',
    access_token: '',
    refresh_token: '',
    token_type: '',
    expires_at: '',
    scope: '',
    redirect_uri: '',
    webhook_url: '',
    api_key: '',
    base_url: '',
    company_id: '',
    procore_budget_view_id: '',
    status: 'active',
    custom_metadata: {},
    enabled_objects: {},
    role: '',
  };
}

function typeLabel(integration) {
  const match = TYPE_OPTIONS.find((row) => row.value === integration.integration_type);
  if (match) return match.label;
  return integrationLabel(integration) || (integration.integration_type || 'Custom').replace(/_/g, ' ');
}

function roleLabel(integration) {
  if (integration.role === 'erp') return 'ERP';
  if (integration.role === 'project_tool') return 'Project tool';
  return '—';
}

function rolePillClass(integration) {
  if (integration.role === 'erp') return 'erp';
  if (integration.role === 'project_tool') return 'project-tool';
  return '';
}

function statusPillClass(status) {
  const text = String(status || '').toLowerCase();
  if (text === 'error') return 'error';
  return statusClass(status);
}

function connectionLabel(integration) {
  if (integration.company_id) return `Company ${integration.company_id}`;
  if (integration.base_url) return integration.base_url;
  return '—';
}

function expiresInputValue(value) {
  if (!value) return '';
  const text = String(value);
  if (text.includes('T')) return text.slice(0, 16);
  return text;
}

function formFromIntegration(integration) {
  return {
    integration_name: integration.integration_name || '',
    integration_type: integration.integration_type || '',
    client_id: integration.client_id || '',
    client_secret: '',
    access_token: '',
    refresh_token: '',
    token_type: integration.token_type || '',
    expires_at: expiresInputValue(integration.expires_at),
    scope: integration.scope || '',
    redirect_uri: integration.redirect_uri || '',
    webhook_url: integration.webhook_url || '',
    api_key: '',
    base_url: integration.base_url || '',
    company_id: integration.company_id || '',
    procore_budget_view_id: integration.procore_budget_view_id || '',
    status: integration.status || 'active',
    custom_metadata: integration.custom_metadata || {},
    enabled_objects: integration.enabled_objects || {},
    role: integration.role || '',
  };
}

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState([]);
  const [availableObjects, setAvailableObjects] = useState({});
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(blankForm());
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [budgetViewLoading, setBudgetViewLoading] = useState(false);
  const [connectionTests, setConnectionTests] = useState({});

  const loadIntegrations = () => {
    setLoading(true);
    setError('');
    return Promise.all([
      getJson('/api/integrations'),
      getJson('/api/integrations/available-objects').catch(() => ({})),
    ])
      .then(([rows, objects]) => {
        setIntegrations(asList(rows));
        setAvailableObjects(objects && typeof objects === 'object' ? objects : {});
      })
      .catch((err) => {
        setError(integrationError(err) || 'Unable to load integrations');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadIntegrations();
  }, []);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return integrations.filter((row) => {
      const matchesSearch =
        !term ||
        [row.integration_name, row.integration_type, row.base_url, row.company_id, roleLabel(row)].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(term)
        );
      const matchesStatus = statusFilter === 'all' || row.status === statusFilter;
      const matchesType = typeFilter === 'all' || row.integration_type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [integrations, query, statusFilter, typeFilter]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setFormData(blankForm());
    setFormErrors({});
    setShowClientSecret(false);
    setBudgetViewLoading(false);
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(blankForm());
    setFormErrors({});
    setShowClientSecret(false);
    setFormOpen(true);
  };

  const openEdit = (integration) => {
    setEditing(integration);
    setFormData(formFromIntegration(integration));
    setFormErrors({});
    setShowClientSecret(false);
    setFormOpen(true);
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleTypeChange = (value) => {
    const spec = integrationSpec({ integration_type: value });
    setFormData((prev) => ({
      ...prev,
      integration_type: value,
      role: spec.role || prev.role,
    }));
    if (formErrors.integration_type) {
      setFormErrors((prev) => ({ ...prev, integration_type: undefined }));
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!formData.integration_name.trim()) nextErrors.integration_name = 'Name is required';
    if (!formData.integration_type.trim()) nextErrors.integration_type = 'Type is required';
    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return;
    }

    setSaving(true);
    setFormErrors({});
    try {
      const payload = { ...formData };
      const saved = editing
        ? await putJson(`/api/integrations/${editing.vuid}`, payload)
        : await postJson('/api/integrations', payload);
      setIntegrations((prev) =>
        editing ? prev.map((row) => (row.vuid === editing.vuid ? saved : row)) : [...prev, saved]
      );
      setFlash({ type: 'ok', text: editing ? 'Integration updated.' : 'Integration created.' });
      closeForm();
    } catch (err) {
      setFormErrors({ submit: integrationError(err) || 'Unable to save the integration' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (integration) => {
    if (!window.confirm(`Delete ${integration.integration_name}?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/integrations/${integration.vuid}`);
      setIntegrations((prev) => prev.filter((row) => row.vuid !== integration.vuid));
      setFlash({ type: 'ok', text: `${integration.integration_name} deleted.` });
      if (editing?.vuid === integration.vuid) closeForm();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) || 'Failed to delete integration' });
    }
  };

  const handleTestConnection = async (integration) => {
    if (integration.integration_type !== 'quickbooks_online') return;
    setConnectionTests((prev) => ({ ...prev, [integration.vuid]: { status: 'testing' } }));
    try {
      const response = await axios.get(`${API_BASE_URL}/api/qbo/test-connection`, {
        params: { integration_vuid: integration.vuid },
      });
      if (response.data.success) {
        const companyName = response.data.company_name || 'QuickBooks Online';
        const message = response.data.mock_mode
          ? `Connected to ${companyName} (mock mode)`
          : `Connected to ${companyName}`;
        setConnectionTests((prev) => ({ ...prev, [integration.vuid]: { status: 'success', message } }));
        setFlash({ type: 'ok', text: message });
      } else {
        const message = response.data.error || 'Connection test failed';
        setConnectionTests((prev) => ({ ...prev, [integration.vuid]: { status: 'error', message } }));
        setFlash({ type: 'err', text: message });
      }
    } catch (err) {
      const message = integrationError(err) || 'Connection test failed';
      setConnectionTests((prev) => ({ ...prev, [integration.vuid]: { status: 'error', message } }));
      setFlash({ type: 'err', text: message });
    }
  };

  const handleRefreshToken = async (integration) => {
    if (!window.confirm(`Refresh the access token for ${integration.integration_name}?`)) return;
    try {
      const data = await postJson(`/api/integrations/${integration.vuid}/refresh-token`);
      if (data.success) {
        const expires = data.expires_at ? new Date(data.expires_at).toLocaleString() : 'N/A';
        setFlash({ type: 'ok', text: `Token refreshed. New expiry: ${expires}` });
        loadIntegrations();
      } else {
        setFlash({ type: 'err', text: data.error || 'Unable to refresh token' });
      }
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) || 'Unable to refresh token' });
    }
  };

  const handleFetchBudgetView = async () => {
    if (!editing?.vuid) {
      setFormErrors({ submit: 'Save the integration before fetching the Procore budget view.' });
      return;
    }
    setBudgetViewLoading(true);
    setFormErrors((prev) => ({ ...prev, submit: undefined }));
    try {
      const updated = await postJson(`/api/integrations/${editing.vuid}/procore-budget-view`);
      setFormData((prev) => ({
        ...prev,
        procore_budget_view_id: updated.procore_budget_view_id || '',
      }));
      setIntegrations((prev) => prev.map((row) => (row.vuid === updated.vuid ? updated : row)));
      setEditing(updated);
    } catch (err) {
      setFormErrors({ submit: integrationError(err) || 'Failed to fetch Procore budget view' });
    } finally {
      setBudgetViewLoading(false);
    }
  };

  return (
    <div className="pc-page">
      <div className="pc-crumb">
        <Link to="/procore">Portfolio</Link>
        {' / '}
        <Link to="/procore/settings">Settings</Link>
        {' / '}
        Setup
        {' / '}
        Integrations
      </div>
      <div className="pc-title-row">
        <h1>Integrations</h1>
        <button type="button" className="pc-btn" onClick={openCreate}>
          Create
        </button>
      </div>
      <div className="pc-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter integrations"
        />
        <label className="pc-toolbar-label">
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="pc-toolbar-label">
          Type
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="all">All</option>
            {TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {flash ? (
        <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`} style={{ marginBottom: 8 }}>
          {flash.text}
        </div>
      ) : null}
      <div className="pc-card">
        {loading ? (
          <div className="pc-empty">Loading…</div>
        ) : error ? (
          <div className="pc-empty">{error}</div>
        ) : visible.length === 0 ? (
          <div className="pc-empty">
            {integrations.length === 0 ? 'No integrations yet.' : 'No integrations match this filter.'}
          </div>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Role</th>
                <th>Status</th>
                <th>Connection</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const test = connectionTests[row.vuid];
                return (
                  <tr key={row.vuid} className="pc-clickable" onClick={() => openEdit(row)}>
                    <td>
                      <button
                        type="button"
                        className="pc-table-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          openEdit(row);
                        }}
                      >
                        {row.integration_name || '—'}
                      </button>
                    </td>
                    <td>{typeLabel(row)}</td>
                    <td>
                      {row.role ? (
                        <span className={`pc-pill ${rolePillClass(row)}`}>{roleLabel(row)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className={`pc-pill ${statusPillClass(row.status)}`}>{row.status || '—'}</span>
                    </td>
                    <td>
                      <span title={row.base_url || ''}>{connectionLabel(row)}</span>
                      {test?.message ? (
                        <div className={`pc-flash-inline ${test.status === 'success' ? 'ok' : 'err'}`}>{test.message}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="pc-table-actions" onClick={(event) => event.stopPropagation()}>
                        {row.integration_type === 'quickbooks_online' ? (
                          <button
                            type="button"
                            className="pc-btn pc-btn-secondary pc-btn-sm"
                            onClick={() => handleTestConnection(row)}
                            disabled={test?.status === 'testing'}
                          >
                            {test?.status === 'testing' ? 'Testing…' : 'Test'}
                          </button>
                        ) : null}
                        {row.integration_type === 'procore' && row.has_refresh_token ? (
                          <button
                            type="button"
                            className="pc-btn pc-btn-secondary pc-btn-sm"
                            onClick={() => handleRefreshToken(row)}
                          >
                            Refresh token
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="pc-btn pc-btn-secondary pc-btn-sm"
                          onClick={() => handleDelete(row)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {formOpen ? (
        <IntegrationFormModal
          editing={editing}
          formData={formData}
          formErrors={formErrors}
          saving={saving}
          availableObjects={availableObjects}
          showClientSecret={showClientSecret}
          budgetViewLoading={budgetViewLoading}
          onClose={closeForm}
          onSave={handleSave}
          onField={setField}
          onTypeChange={handleTypeChange}
          onToggleSecret={() => setShowClientSecret((value) => !value)}
          onFetchBudgetView={handleFetchBudgetView}
          onTest={() => editing && handleTestConnection(editing)}
          test={editing ? connectionTests[editing.vuid] : null}
        />
      ) : null}
    </div>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <label className="pc-form-row">
      <span>{label}</span>
      {children}
      {hint ? <span className="pc-form-hint">{hint}</span> : null}
      {error ? <span className="pc-form-error">{error}</span> : null}
    </label>
  );
}

function IntegrationFormModal({
  editing,
  formData,
  formErrors,
  saving,
  availableObjects,
  showClientSecret,
  budgetViewLoading,
  onClose,
  onSave,
  onField,
  onTypeChange,
  onToggleSecret,
  onFetchBudgetView,
  onTest,
  test,
}) {
  const objectEntries = Object.entries(availableObjects || {});
  const isProcore = formData.integration_type === 'procore';
  const isQbo = formData.integration_type === 'quickbooks_online';
  const [showCredentials, setShowCredentials] = useState(Boolean(editing));

  return (
    <div className="pc-modal-backdrop" onClick={() => !saving && onClose()}>
      <div className="pc-modal pc-modal-wide" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>{editing ? 'Edit integration' : 'Create integration'}</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={onSave}>
          <div className="pc-modal-body">
            <div className="pc-form-grid">
              <Field label="Name *" error={formErrors.integration_name}>
                <input
                  name="integration_name"
                  value={formData.integration_name}
                  onChange={(event) => onField('integration_name', event.target.value)}
                  placeholder="e.g. QuickBooks Online"
                />
              </Field>
              <Field label="Type *" error={formErrors.integration_type}>
                <select
                  value={formData.integration_type}
                  onChange={(event) => onTypeChange(event.target.value)}
                >
                  <option value="">Select type</option>
                  {TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Accounting role"
                hint="ERP is used for accounting sync and journals. Project tools stay on job-level sync."
              >
                <select value={formData.role} onChange={(event) => onField('role', event.target.value)}>
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value || 'infer'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select value={formData.status} onChange={(event) => onField('status', event.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="pc-form-section">
              <h3>Connection</h3>
            </div>
            <div className="pc-form-grid">
              <Field label="Base URL">
                <input
                  type="url"
                  value={formData.base_url}
                  onChange={(event) => onField('base_url', event.target.value)}
                  placeholder="https://…"
                />
              </Field>
              <Field label="Company ID" hint="Required for Procore and similar company-scoped APIs.">
                <input
                  value={formData.company_id}
                  onChange={(event) => onField('company_id', event.target.value)}
                  placeholder="Company / tenant ID"
                />
              </Field>
              <Field label="Client ID">
                <input
                  value={formData.client_id}
                  onChange={(event) => onField('client_id', event.target.value)}
                  placeholder="OAuth client ID"
                />
              </Field>
              <Field label="Client secret">
                <div className="pc-secret-row">
                  <input
                    type={showClientSecret ? 'text' : 'password'}
                    value={formData.client_secret}
                    onChange={(event) => onField('client_secret', event.target.value)}
                    placeholder="OAuth client secret"
                  />
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={onToggleSecret}>
                    {showClientSecret ? 'Hide' : 'Show'}
                  </button>
                </div>
              </Field>
            </div>

            <div className="pc-form-section">
              <div className="pc-form-section-row">
                <h3>Credentials</h3>
                <button
                  type="button"
                  className="pc-btn pc-btn-secondary pc-btn-sm"
                  onClick={() => setShowCredentials((value) => !value)}
                >
                  {showCredentials ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {showCredentials ? (
            <div className="pc-form-grid">
              <Field label="Access token">
                <input
                  type="password"
                  value={formData.access_token}
                  onChange={(event) => onField('access_token', event.target.value)}
                />
              </Field>
              <Field label="Refresh token">
                <input
                  type="password"
                  value={formData.refresh_token}
                  onChange={(event) => onField('refresh_token', event.target.value)}
                />
              </Field>
              <Field label="Token type">
                <input
                  value={formData.token_type}
                  onChange={(event) => onField('token_type', event.target.value)}
                  placeholder="Bearer"
                />
              </Field>
              <Field label="Expires at">
                <input
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(event) => onField('expires_at', event.target.value)}
                />
              </Field>
              <Field label="Scope">
                <input
                  value={formData.scope}
                  onChange={(event) => onField('scope', event.target.value)}
                />
              </Field>
              <Field label="Redirect URI">
                <input
                  type="url"
                  value={formData.redirect_uri}
                  onChange={(event) => onField('redirect_uri', event.target.value)}
                />
              </Field>
              <Field label="Webhook URL">
                <input
                  type="url"
                  value={formData.webhook_url}
                  onChange={(event) => onField('webhook_url', event.target.value)}
                />
              </Field>
              <Field label="API key">
                <input
                  type="password"
                  value={formData.api_key}
                  onChange={(event) => onField('api_key', event.target.value)}
                />
              </Field>
            </div>
            ) : null}

            {isProcore ? (
              <>
                <div className="pc-form-section">
                  <h3>Procore</h3>
                </div>
                <Field label="Budget view ID">
                  <div className="pc-secret-row">
                    <input
                      value={formData.procore_budget_view_id}
                      onChange={(event) => onField('procore_budget_view_id', event.target.value)}
                      placeholder="Standard Budget view ID"
                    />
                    <button
                      type="button"
                      className="pc-btn pc-btn-secondary pc-btn-sm"
                      onClick={onFetchBudgetView}
                      disabled={budgetViewLoading}
                    >
                      {budgetViewLoading ? 'Fetching…' : 'Fetch'}
                    </button>
                  </div>
                </Field>
              </>
            ) : null}

            {isQbo && editing ? (
              <div className="pc-form-section">
                <button
                  type="button"
                  className="pc-btn pc-btn-secondary"
                  onClick={onTest}
                  disabled={test?.status === 'testing'}
                >
                  {test?.status === 'testing' ? 'Testing…' : 'Test connection'}
                </button>
                {test?.message ? (
                  <div className={`pc-flash-inline ${test.status === 'success' ? 'ok' : 'err'}`} style={{ marginTop: 8 }}>
                    {test.message}
                  </div>
                ) : null}
              </div>
            ) : null}

            {objectEntries.length ? (
              <>
                <div className="pc-form-section">
                  <h3>Objects</h3>
                  <p>Choose which records can send or receive through this integration.</p>
                </div>
                <div className="pc-object-checks">
                  {objectEntries.map(([key, obj]) => (
                    <label key={key} className="pc-object-check">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.enabled_objects?.[key])}
                        onChange={(event) =>
                          onField('enabled_objects', {
                            ...formData.enabled_objects,
                            [key]: event.target.checked,
                          })
                        }
                      />
                      <span>
                        <strong>{obj.name || key}</strong>
                        <span>{obj.description}</span>
                        {Array.isArray(obj.operations) && obj.operations.length ? (
                          <span>{obj.operations.join(', ')}</span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}

            {formErrors.submit ? <div className="pc-flash pc-flash-err">{formErrors.submit}</div> : null}
          </div>
          <div className="pc-modal-actions">
            <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="pc-btn" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
